import { useEffect, useRef } from 'react'

import { ResultCode, StepNames } from '@src/enums'
import { createContextLogger } from '@src/utils'
import { useApplicationStore, useHistoryStore } from '@src/zustand_stores'

// TODO: Add more time in case of CCTP
const TIMEOUT_LIMIT = 60 * 10 // To debug, set to a lower value.
const logger = createContextLogger('useFailOnTimeout')

export function useFailOnTimeout() {
    const { exchangeId } = useApplicationStore()
    const updateHistory = useHistoryStore(state => state.updateHistory)
    const removeHistory = useHistoryStore(state => state.removeHistory)
    const getOrder = useHistoryStore(state => state.getOrder)
    const transactionData = getOrder(exchangeId)

    // Ref to hold the timeout identifier.
    const timeoutRef = useRef<NodeJS.Timeout | null>(null)

    const clearTimeoutTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    // Ref to track if the pending state timer has been started.
    const timerStartedRef = useRef(false)

    useEffect(() => {
        // Log the transaction data for debugging.
        if (transactionData) {
            logger.info(
                `transactionData.exchangeId = ${transactionData.exchangeId}\n` +
                    `transactionData.currentStep = ${transactionData.currentStep}\n` +
                    `transactionData.result = ${JSON.stringify(transactionData.result)}`
            )
        }
    }, [transactionData])

    useEffect(() => {
        // Only act if transactionData exists.
        if (!transactionData) return

        // Check if we are in a pending state.
        const isPending =
            transactionData.currentStep === StepNames.MATCHING ||
            transactionData.currentStep === StepNames.MATCH_CREATED

        // Check if transaction has succeeded.
        const isSuccess =
            transactionData.currentStep === StepNames.MATCH_EXECUTED &&
            transactionData.result?.status === ResultCode.success

        if (isPending && !timerStartedRef.current) {
            // Start the timer only once when first entering a pending state.
            logger.info(`Starting timeout watcher for exchangeId ${exchangeId} at step ${transactionData.currentStep}`)
            timerStartedRef.current = true
            timeoutRef.current = setTimeout(() => {
                logger.error(`Transaction timed out for exchangeId ${exchangeId}`)
                updateHistory(exchangeId, {
                    currentStep: StepNames.MATCH_TIMED_OUT,
                    result: {
                        status: ResultCode.failure,
                        message: 'Your swap has timed out. Please try again.',
                    },
                })
            }, TIMEOUT_LIMIT * 1000)

            clearTimeoutTimeoutRef.current = setTimeout(
                () => {
                    logger.error(`Clearing timeout to avoid memory leak`)
                    timeoutRef.current = null
                    clearTimeoutTimeoutRef.current = null
                },
                2 * TIMEOUT_LIMIT * 1000
            )
            logger.info(`Timeout started for ${exchangeId}`)
        } else if (isSuccess) {
            // If the transaction succeeded, clear any pending timer and reset our flag.
            logger.info(`Transaction succeeded for exchangeId ${exchangeId} - clearing timeout.`)
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
                timeoutRef.current = null
                clearTimeoutTimeoutRef.current = null
                logger.info('Timeout cleared.')
            }
            timerStartedRef.current = false
        }
    }, [transactionData, exchangeId, updateHistory])

    return null // This hook doesn't render anything.
}
